import { useEffect, useRef, useState } from 'react';
import { api, type PhotoMeta } from '../api.js';
import { t } from '../i18n.js';
import { PhotoLightbox } from './PhotoLightbox.js';

export function MemoriesSection() {
  const [photos, setPhotos] = useState<PhotoMeta[]>([]);
  const [active, setActive] = useState<PhotoMeta | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const reloadPhotos = () => {
    void api.listPhotos().then((r) => setPhotos(r.photos)).catch(() => {});
  };

  useEffect(() => { reloadPhotos(); }, []);

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const result = await api.uploadPhotos(Array.from(fileList));
      if (result.rejected.length > 0) {
        setError(t('memories.upload_failed', { names: result.rejected.map((r) => r.name).join(', ') }));
      }
      reloadPhotos();
    } catch {
      setError(t('memories.upload_error'));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <div className="section-page">
      <section className="gallery">
        <div className="gallery-header">
          <h1 className="section-title">{t('memories.title')}</h1>
          <button
            className="gallery-add"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
          >
            {uploading ? t('memories.uploading') : `+ ${t('memories.add_photos')}`}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => void handleFiles(e.target.files)}
          />
        </div>

        {error && <div className="banner">{error}</div>}

        {photos.length === 0 ? (
          <p className="gallery-empty">{t('memories.empty')}</p>
        ) : (
          <div className="gallery-grid">
            {photos.map((p) => (
              <button key={p.id} className="gallery-thumb" onClick={() => setActive(p)}>
                <img src={p.thumb_url} alt={new Date(p.created_at * 1000).toLocaleDateString()} loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </section>

      {active && (
        <PhotoLightbox
          photo={active}
          onClose={() => setActive(null)}
          onDeleted={() => { setActive(null); reloadPhotos(); }}
        />
      )}
    </div>
  );
}
